/**
 * Client-side image compression for uploads.
 *
 * Takes a File/Blob; if it's a bitmap image larger than the threshold,
 * resizes it down to fit within MAX_DIMENSION and re-encodes as JPEG at
 * QUALITY.  Non-image files — and images where the compressed version is
 * NOT smaller than the original (rare but possible for already-optimised
 * PNGs, tiny thumbnails, SVGs, animated GIFs, HEIC without a codec) —
 * are returned unchanged so nothing is ever silently degraded.
 *
 * No third-party dependencies — pure browser Canvas API.
 */

const MAX_DIMENSION = 2000;          // longest side, in pixels
const QUALITY = 0.85;                // JPEG quality 0–1
const MIN_SIZE_TO_COMPRESS = 300 * 1024; // < 300 KB: skip (not worth it)

// MIME types we will try to re-encode.  SVG + animated GIFs are excluded on
// purpose because they either vectorise or lose frames in canvas re-encoding.
const COMPRESSIBLE = new Set(["image/jpeg", "image/jpg", "image/png", "image/webp", "image/heic", "image/heif"]);

export function isCompressibleImage(file) {
  return !!file && COMPRESSIBLE.has((file.type || "").toLowerCase());
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (err) => { URL.revokeObjectURL(url); reject(err); };
    img.src = url;
  });
}

function canvasToBlob(canvas, mime, quality) {
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), mime, quality));
}

/**
 * Compress a single image file. Resolves with the ORIGINAL file when:
 *   - it's not a compressible image
 *   - it's smaller than MIN_SIZE_TO_COMPRESS
 *   - it's already within MAX_DIMENSION and the compressed version is bigger
 *   - the browser fails to decode (e.g. HEIC with no native codec)
 * Resolves with a new File when compression actually saves bytes.
 */
export async function compressImage(file, opts = {}) {
  if (!isCompressibleImage(file)) return file;
  if (file.size < (opts.minSize ?? MIN_SIZE_TO_COMPRESS)) return file;

  try {
    const img = await loadImage(file);
    const maxDim = opts.maxDimension ?? MAX_DIMENSION;
    const longest = Math.max(img.width, img.height);
    const scale = longest > maxDim ? maxDim / longest : 1;
    const w = Math.round(img.width * scale);
    const h = Math.round(img.height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);

    const quality = opts.quality ?? QUALITY;
    const blob = await canvasToBlob(canvas, "image/jpeg", quality);

    if (!blob || blob.size >= file.size) return file; // no gain, keep original

    const newName = file.name.replace(/\.(png|webp|heic|heif|jpeg|jpg)$/i, "") + ".jpg";
    return new File([blob], newName, { type: "image/jpeg", lastModified: Date.now() });
  } catch (e) {
    // Decode failed — likely HEIC without codec; hand the original back so
    // the server still receives something.
    return file;
  }
}

/**
 * Compress a list of files, returning {files, saved} where `saved` is total
 * bytes shaved off. Use this when the user selects multiple images at once.
 */
export async function compressFileList(files, opts = {}) {
  const arr = Array.from(files || []);
  const out = [];
  let saved = 0;
  for (const f of arr) {
    const before = f.size;
    const result = await compressImage(f, opts);
    out.push(result);
    if (result !== f) saved += Math.max(0, before - result.size);
  }
  return { files: out, saved };
}

export function formatBytes(n) {
  if (!n || n < 1024) return `${n || 0} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
